import { useState, useMemo, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSnackbar } from 'notistack'
import { map, filter } from 'lodash'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { useTeam } from '../../contexts/TeamContext'
import useNodesResources from '../../shared/resources/api/v1/admin/useNodesResources'
import useInterfacesResources from '../../shared/resources/api/v1/admin/networking/useInterfacesResources'
import useAddressesResources from '../../shared/resources/api/v1/admin/networking/useAddressesResources'
import { useConfirmationDialog } from '../../system/ConfirmationDialogProvider'
import LoadingState from '../../shared/components/LoadingState'
import ErrorState from '../../shared/components/ErrorState'
import PageHeader from '../../shared/components/layouts/PageHeader'
import PageBody from '../../shared/components/layouts/PageBody'
import TabPanel from '../../shared/components/TabPanel'
import ResponsiveTabs from '../../shared/components/ResponsiveTabs'
import NodeGeneralTab from './components/NodeGeneralTab'
import NodeStatisticsTab from './components/NodeStatisticsTab'
import NodeNetworkTab from './components/NodeNetworkTab'
import NodeFeaturesTab from './components/NodeFeaturesTab'

function NodeShowPage() {
  const { teamSlug, nodeId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const { team } = useTeam()
  const { get, destroy } = useNodesResources(teamSlug)
  const { all: allInterfaces } = useInterfacesResources(teamSlug)
  const { all: allAddresses } = useAddressesResources(teamSlug)
  const { openConfirmationDialog } = useConfirmationDialog()
  const [node, setNode] = useState(null)
  const [interfaces, setInterfaces] = useState([])
  const [addresses, setAddresses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const getTabValue = () => {
    if (location.pathname.endsWith('/statistics')) return 1
    if (location.pathname.endsWith('/networking')) return 2
    if (location.pathname.endsWith('/features')) return 3
    return 0
  }
  const tabValue = getTabValue()

  useEffect(() => {
    setLoading(true)
    setError(null)
    Promise.all([
      get(nodeId),
      allInterfaces({ params: { nodeId } }),
      allAddresses({ params: { nodeId } })
    ])
      .then(([nodeResponse, interfacesResponse, addressesResponse]) => {
        setNode(nodeResponse.node)
        setInterfaces(interfacesResponse.interfaces || [])
        setAddresses(addressesResponse.addresses || [])
      })
      .catch((err) => {
        setError(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [teamSlug, nodeId])

  const handleDelete = useCallback(() => {
    openConfirmationDialog({
      title: <FormattedMessage id="nodes.delete.title" />,
      message: <FormattedMessage id="nodes.delete.message" />,
      onConfirm: () => destroy(nodeId)
        .then(() => {
          navigate(`/teams/${teamSlug}/nodes`)
        })
        .catch(() => {
          enqueueSnackbar(formatMessage({ id: 'nodes.delete.error' }), { variant: 'error' })
        }),
      onCancel: () => Promise.resolve(),
    })
  }, [openConfirmationDialog, destroy, nodeId, navigate, teamSlug, enqueueSnackbar, formatMessage])

  const networkInterfaces = useMemo(() => {
    return map(interfaces, (iface) => ({
      ...iface,
      addresses: filter(addresses, (addr) => addr.interfaceId === iface.id)
    }))
  }, [interfaces, addresses])

  if (loading) return <LoadingState />
  if (error) return <ErrorState messageId="nodes.error" />

  const basePath = `/teams/${teamSlug}/nodes/${nodeId}`

  const tabs = useMemo(() => [
    { label: <FormattedMessage id="nodes.tabs.general" />, to: basePath },
    { label: <FormattedMessage id="nodes.tabs.statistics" />, to: `${basePath}/statistics` },
    { label: <FormattedMessage id="nodes.tabs.networking" />, to: `${basePath}/networking` },
    { label: <FormattedMessage id="nodes.tabs.features" />, to: `${basePath}/features` },
  ], [basePath])

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <PageHeader
        title={node.name}
        entities={{ team, node }}
        actions={
          <Button
            onClick={handleDelete}
            variant="outlined"
            color="error"
            size="small"
          >
            <FormattedMessage id="nodes.delete.button" />
          </Button>
        }
      />

      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        overflow: 'hidden',
        minHeight: 0
      }}>
        <ResponsiveTabs tabs={tabs} value={tabValue} />

        <PageBody disableContainer>
          <TabPanel value={tabValue} index={0}>
            <NodeGeneralTab node={node} addresses={addresses} />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <NodeStatisticsTab
              teamSlug={teamSlug}
              nodeId={nodeId}
              active={tabValue === 1}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <NodeNetworkTab networkInterfaces={networkInterfaces} />
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <NodeFeaturesTab
              teamSlug={teamSlug}
              nodeId={nodeId}
              active={tabValue === 3}
            />
          </TabPanel>
        </PageBody>
      </Box>
    </Box>
  )
}

export default NodeShowPage
